<div
  class="bg-white dark:bg-transparent dark:bg-slate-900 h-full flex flex-col border-t border-gray-200 dark:border-gray-800">
  <form class="flex flex-col flex-grow p-6 w-full" [formGroup]="taskForm">
    <!--Title / Subject + Type-->
    <div class="mb-3 flex flex-col md:flex-row gap-2 md:items-start">
      <mat-form-field
        [class]="taskTypeCode !== 'message' ? 'w-full md:w-9/12' : 'w-full'"
        [subscriptSizing]="'dynamic'"
        hideRequiredMarker="true">
        @if (isReplying || isMessageTask) {
          <mat-label>Subject</mat-label>
        } @else {
          <mat-label>Title</mat-label>
        }
        <input matInput formControlName="task_label" cdkTextareaAutosize="true" [spellcheck]="true" />
        <mat-error class="pb-2" *ngIf="taskForm.get('task_label').hasError('required')">Value is required.</mat-error>
      </mat-form-field>

      <!--Type-->
      @if (taskTypeCode !== 'message') {
        <mat-form-field
          class="w-full md:w-3/12 cipx-select-no-extra-mb mb-3 md:mb-0"
          appearance="fill"
          [subscriptSizing]="'dynamic'"
          hideRequiredMarker="true">
          <mat-label>Type</mat-label>
          <mat-select formControlName="task_type_id" required>
            <mat-option value="">Select a task type</mat-option>
            <mat-option *ngFor="let type of visibleTaskTypes" [value]="type.task_type_id">
              {{ type.task_type_label }}
            </mat-option>
          </mat-select>
          <mat-error *ngIf="taskForm.get('task_type_id')?.hasError('required')">Type is required</mat-error>
        </mat-form-field>
      }
    </div>

    <div class="mb-3">
      @if (qrAssociatedToTask?.quote_request_id) {
        <app-quote-request-task-association-view
          [quoteRequestTaskAssociation]="qrAssociatedToTask"
          (disassociateQuoteRequestEvent)="onDetachQuoteRequest()"></app-quote-request-task-association-view>
      } @else {
        <app-quote-request-search-input
          [label]="'Quote Requests'"
          [accountId]="selectedAccount?.account_id"
          (recordClickEvent)="appendQRRelatedEntity($event)"></app-quote-request-search-input>
      }
    </div>

    <!-- Assign To / Only exist if creating task from Account User Manage Interface-->
    <div class="w-full flex flex-col gap-1 mb-3">
      <!--Created By To-->
      <span class="font-semibold">
        {{ isReplying ? 'Assigned To' : isMessageTask ? 'To' : 'Assign to' }}
      </span>
      <div class="w-full flex flex-col md:flex-row gap-1">
        @if (isMessageTask && (selectedAccount?.account_users || accountUsersList.length > 0)) {
          <div class="w-full md:w-6/12">
            <cipx-searchable-select
              [formGroup]="taskForm"
              controlName="account_user"
              [options]="accountUsersListWithFullNames"
              valueKey="account_user_id"
              labelKey="account_user_full_name"
              [multiple]="true"
              (selectionChange)="onAssigneeSelected($event, 'account_user')"></cipx-searchable-select>
          </div>
        }

        @if (taskTypeCode !== 'message') {
          <div class="w-full md:w-6/12">
            <cipx-searchable-select
              [formGroup]="taskForm"
              controlName="assign_to"
              label="Admins"
              [options]="adminUsersWithFullNames"
              valueKey="admin_user_id"
              labelKey="user_full_name"
              [multiple]="true"
              (selectionChange)="onAssigneeSelected($event)"></cipx-searchable-select>
          </div>
          <div class="w-full md:w-6/12">
            <cipx-searchable-select
              [formGroup]="taskForm"
              controlName="team"
              label="Teams"
              [options]="teams"
              valueKey="team_id"
              labelKey="team_label"
              [multiple]="true"
              (selectionChange)="onAssigneeSelected($event, 'team')"></cipx-searchable-select>
          </div>
        }
      </div>
      @if (assigneesMissing) {
        <mat-error class="text-xs">At least one assignee is required.</mat-error>
      }
    </div>

    <!-- Due Date and Time (On the Same Line) -->
    @if (taskTypeCode !== 'message') {
      <div class="mb-3">
        <sbd-doug-frontend-task-date-time-picker
          [formGroup]="taskForm"
          (onDateTimePicked)="setDatePickedDateTime($event)"></sbd-doug-frontend-task-date-time-picker>
      </div>
    }

    <!--Description / Message-->
    <div class="border-b mb-4">
      <mat-label class="font-medium">{{ isMessageTask ? 'Message' : 'Description' }}</mat-label>
      <tiny-editor
        [editorOptions]="{ min_height: 400, max_height: 800, resize: true }"
        class="block"
        controlName="task_description"
        editorType="basic"></tiny-editor>
      <mat-error
        class="text-xs"
        *ngIf="taskForm.get('task_description')?.touched && taskForm.get('task_description')?.hasError('required')">
        Description is required
      </mat-error>
    </div>

    <!--Secure Message Checkbox-->
    <div class="mb-2 pb-2" *ngIf="shouldShowSecureMessageCheckbox()">
      <div class="flex items-center gap-2">
        <mat-checkbox formControlName="send_secure_message" [color]="'primary'">Send Secure Message</mat-checkbox>
        <mat-icon
          [matTooltip]="isSecureMessageEnabled ? 'If checked, the account user will need to log in to read your message.' : 'Secure messaging is only available for Account Users.'"
          [attr.aria-label]="isSecureMessageEnabled ? 'If checked, the account user will need to log in to read your message.' : 'Secure messaging is only available for Account Users.'">
          info
        </mat-icon>
      </div>
    </div>

    <!--File Uploader Area-->
    <div class="my-3">
      <button mat-raised-button class="flex items-center mt-4" (click)="openFileUploader()">
        <mat-icon>upload</mat-icon>
        <span class="ml-2 mr-1">Upload Files</span>
      </button>
    </div>
    <!-- Account User Reserved -->
    @if (queuedFiles?.length > 0) {
      <div class="w-full flex flex-col md:flex-row">
        <div class="my-1 w-2/3">
          <span class="my-2 font-semibold">Selected files</span>
          <div class="grid grid-cols-1 gap-2 items-center justify-start">
            <div *ngFor="let file of queuedFiles; let i = index">
              <div class="flex flex-row items-center">
                <button mat-icon-button class="cursor-pointer" color="warn" (click)="deleteFile(i)">
                  <mat-icon>delete</mat-icon>
                </button>
                <p class="truncate" rel="noopener noreferrer flex flex-row items-center">
                  <b>(Documentation)</b>
                  {{ file.name }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  </form>

  @if (showFooter) {
    <sbd-doug-frontend-common-form-footer
      (cancel)="moveBackToParent()"
      (save)="submitForm()"
      [cancelText]="'Cancel'"
      [saveText]="saveButtonText"></sbd-doug-frontend-common-form-footer>
  }
</div>
